import Phaser from 'phaser';
import { generateGameTextures } from '../assets/textureGenerator';

export class BootScene extends Phaser.Scene {
  constructor() {
    super({ key: 'BootScene' });
  }

  preload() {
    const { width, height } = this.scale;

    // Title banner
    this.add.text(width / 2, height / 2 - 50, '⚡ SUMMONING ULTRA ZELVIS ⚡', {
      fontFamily: 'Impact, Arial Black, sans-serif',
      fontSize: '24px',
      color: '#facc15',
      stroke: '#b45309',
      strokeThickness: 5,
      align: 'center'
    }).setOrigin(0.5);

    // Subtitle
    this.add.text(width / 2, height / 2 - 16, "KING OF ROCK 'N OLYMPUS", {
      fontFamily: 'Arial, sans-serif',
      fontSize: '13px',
      color: '#38bdf8',
      fontStyle: 'bold',
      letterSpacing: 2
    }).setOrigin(0.5);

    // Progress Bar Background
    const progressBox = this.add.graphics();
    progressBox.fillStyle(0x1e293b, 0.85);
    progressBox.fillRoundedRect(width / 2 - 150, height / 2 + 25, 300, 24, 12);
    progressBox.lineStyle(2, 0x38bdf8, 0.8);
    progressBox.strokeRoundedRect(width / 2 - 150, height / 2 + 25, 300, 24, 12);

    const progressBar = this.add.graphics();

    // Progress Percentage Text
    const percentText = this.add.text(width / 2, height / 2 + 70, 'LOADING: 0%', {
      fontFamily: 'Impact, Arial Black, sans-serif',
      fontSize: '16px',
      color: '#ffffff'
    }).setOrigin(0.5);

    this.load.on('progress', (val) => {
      progressBar.clear();
      progressBar.fillStyle(0xfacc15, 1);
      progressBar.fillRoundedRect(width / 2 - 146, height / 2 + 29, Math.max(12, 292 * val), 16, 8);
      percentText.setText(`LOADING: ${Math.floor(val * 100)}%`);
    });

    this.load.on('complete', () => {
      percentText.setText('⚡ ROCK READY! ⚡');
    });

    // Load artwork & backgrounds
    this.load.image('zelvis_hero', 'assets/zelvis_hero.jpg');
    this.load.image('olympus_bg', 'assets/olympus_bg.jpg');
    this.load.image('tartarus_bg', 'assets/tartarus_bg.jpg');
    this.load.image('hephaestus_bg', 'assets/hephaestus_bg.jpg');
    this.load.image('cosmic_bg', 'assets/cosmic_bg.jpg');
    this.load.image('atlantis_bg', 'assets/atlantis_bg.jpg');
    this.load.image('dionysus_bg', 'assets/dionysus_bg.jpg');
    this.load.image('ares_bg', 'assets/ares_bg.jpg');
    this.load.image('chronos_bg', 'assets/chronos_bg.jpg');
    this.load.image('styx_bg', 'assets/styx_bg.jpg');
    this.load.image('apollo_bg', 'assets/apollo_bg.jpg');
    this.load.image('chaos_bg', 'assets/chaos_bg.jpg');

    // Load ultra-detailed nanobanana2 sprites
    this.load.image('zelvis_idle', 'assets/zelvis_anims/zelvis_idle.png');
    this.load.image('zelvis_run_1', 'assets/zelvis_anims/zelvis_run_1.png');
    this.load.image('zelvis_run_2', 'assets/zelvis_anims/zelvis_run_2.png');
    this.load.image('zelvis_run_3', 'assets/zelvis_anims/zelvis_run_3.png');
    this.load.image('zelvis_run_4', 'assets/zelvis_anims/zelvis_run_4.png');
    this.load.image('zelvis_slide', 'assets/zelvis_anims/zelvis_slide.png');
    this.load.image('zelvis_skid_1', 'assets/zelvis_anims/zelvis_skid_1.png');
    this.load.image('zelvis_skid_2', 'assets/zelvis_anims/zelvis_skid_2.png');
    this.load.image('zelvis_fly', 'assets/zelvis_anims/zelvis_fly.png');
    this.load.image('zelvis_guitar', 'assets/zelvis_anims/zelvis_guitar.png');
    this.load.image('zelvis_heroic', 'assets/zelvis_anims/zelvis_heroic.png');
  }

  create() {
    console.log('[BootScene] create start');
    // Generate platforms, particles, and UI textures
    generateGameTextures(this);
    console.log('[BootScene] generateGameTextures finished');

    // 1. Ultra-detailed Running Animation (4 fluid frames)
    if (!this.anims.exists('run')) {
      this.anims.create({
        key: 'run',
        frames: [
          { key: 'zelvis_run_1' },
          { key: 'zelvis_run_2' },
          { key: 'zelvis_run_3' },
          { key: 'zelvis_run_4' }
        ],
        frameRate: 10,
        repeat: -1
      });
    }

    // 2. Neutral Idle Animation
    if (!this.anims.exists('idle')) {
      this.anims.create({
        key: 'idle',
        frames: [{ key: 'zelvis_idle' }],
        frameRate: 1
      });
    }

    // 3. Rockstar Knee Slide Animation
    if (!this.anims.exists('slide')) {
      this.anims.create({
        key: 'slide',
        frames: [{ key: 'zelvis_slide' }],
        frameRate: 1
      });
    }

    // 4. Braking / Skid Animation (Sparks & Dust)
    if (!this.anims.exists('skid')) {
      this.anims.create({
        key: 'skid',
        frames: [
          { key: 'zelvis_skid_1' },
          { key: 'zelvis_skid_2' }
        ],
        frameRate: 8,
        repeat: -1
      });
    }

    // 5. Anti-Gravity Mid-air Flying Animation
    if (!this.anims.exists('flip')) {
      this.anims.create({
        key: 'flip',
        frames: [{ key: 'zelvis_fly' }],
        frameRate: 1
      });
    }

    // 6. Guitar Solo / Shred Victory Animation
    if (!this.anims.exists('guitar')) {
      this.anims.create({
        key: 'guitar',
        frames: [{ key: 'zelvis_guitar' }],
        frameRate: 1
      });
    }

    // Start scene (supports ?stage=N for direct level inspection)
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.has('stage')) {
      const lvl = parseInt(urlParams.get('stage'), 10) || 1;
      this.scene.start('GameScene', { level: lvl });
    } else {
      this.scene.start('MenuScene');
    }
  }
}
